import { Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalToast } from "../../context/ToastProvider";
import { updateFleetDetails } from "../../services/fleetDetailsFirestore";

const SAVE_DEBOUNCE_MS = 650;

export default function ColorPicker({ labelKey, color, onChange, colorType, onClose, fleetId, disabled = false }) {
  const toast = useGlobalToast();
  const { t } = useTranslation();
  const [draftColor, setDraftColor] = useState(color);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimerRef = useRef(null);
  const previewFrameRef = useRef(null);
  const pendingPreviewColorRef = useRef(color);
  const lastSavedColorRef = useRef(color);
  const saveSequenceRef = useRef(0);

  useEffect(() => {
    setDraftColor(color);
  }, [color]);

  useEffect(() => () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    if (previewFrameRef.current) {
      cancelAnimationFrame(previewFrameRef.current);
    }
  }, []);

  const saveColor = async (newColor) => {
    if (disabled || !newColor || newColor === lastSavedColorRef.current) return;
    if (!fleetId) return;

    const saveSequence = saveSequenceRef.current + 1;
    saveSequenceRef.current = saveSequence;
    setIsSaving(true);
    try {
      await updateFleetDetails(fleetId, { [colorType]: newColor });
      lastSavedColorRef.current = newColor;

      toast({
        title: t("feedback.colorSaved", { field: t(labelKey) }),
        status: "success",
      });

      if (onClose) onClose();
    } catch (error) {
      setDraftColor(lastSavedColorRef.current);
      onChange(lastSavedColorRef.current);
      toast({ error,
        title: t("feedback.colorFailed", { field: t(labelKey) }),
        status: "error",
      });
      console.log("Error saving color:", error);
    } finally {
      if (saveSequenceRef.current === saveSequence) {
        setIsSaving(false);
      }
    }
  };

  const scheduleColorSave = (newColor) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      saveColor(newColor);
    }, SAVE_DEBOUNCE_MS);
  };

  const schedulePreview = (newColor) => {
    pendingPreviewColorRef.current = newColor;
    if (previewFrameRef.current) return;
    previewFrameRef.current = requestAnimationFrame(() => {
      previewFrameRef.current = null;
      onChange(pendingPreviewColorRef.current);
    });
  };

  const handleColorChange = (newColor) => {
    setDraftColor(newColor);
    schedulePreview(newColor);
    scheduleColorSave(newColor);
  };

  const handleColorCommit = (newColor) => {
    if (previewFrameRef.current) {
      cancelAnimationFrame(previewFrameRef.current);
      previewFrameRef.current = null;
    }
    onChange(newColor);
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    saveColor(newColor);
  };

  return (
    <div>
      <Typography.Text strong>{t(labelKey)}</Typography.Text>
      <div style={{ marginTop: 6 }}>
        <input
          type="color"
          value={draftColor}
          onChange={(e) => handleColorChange(e.target.value)}
          onBlur={(e) => handleColorCommit(e.target.value)}
          disabled={disabled}
          aria-busy={isSaving}
          style={{
            width: "30px",
            height: "30px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        />
      </div>
    </div>
  );
}
